import {
  setPlumaContextValue,
  setPlumaTheme,
  setPlumaIconManager,
  plumaContextValueStore as legacyPlumaContextValueStore,
  plumaThemeStore as legacyPlumaThemeStore,
  plumaIconManagerStore as legacyPlumaIconManagerStore,
} from '@customerio/hydra-pluma-state';
import { IconManager } from '@customerio/pluma-icons';
import { defaultTheme } from '@customerio/pluma-components/css';
import { PlumaProvider, GlobalThemeManager, usePlumaColorScheme } from '@customerio/pluma-components/react';
import type { ColorSchemePreference, PlumaContextValue, ResolvedColorScheme } from '@customerio/pluma-components/react';
import type { PlumaTheme, PartialPlumaTheme } from '@customerio/pluma-components/css';
import { useLocation, useRouter } from '@tanstack/react-router';
import { atom } from 'nanostores';
import { useStore } from '@nanostores/react';
import { useEffect, useMemo, useRef, type Ref } from 'react';
import type { LinkComponent, PlumaProviderProps } from '@customerio/pluma-components/react';
import { appendMasqParam } from '@/utils/masq';
import { useOptionalSafeNavigate } from '@/services/router/safe-navigate';
import { isEmptyString } from '@/utils/string';
import { shouldProcessLinkClick } from '../../utils/navigation';
import { useThemeContext } from '@/context/theme';
import { snackbarApi } from '@/services/snackbar';
import merge from 'lodash-es/merge';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { defaultPlumaThemes, getPlumaThemes, type ResolvedPlumaThemes } from './pluma-themes';

const iconManager = new IconManager();
setPlumaIconManager(iconManager);

const UNSAFE_ICON_SIZE_OVERRIDE_LS_KEY = 'hydra:unsafe_icon_size_override';
let defaultIconSizeVersion: 'v1' | 'v2' = 'v2';
const iconSizeOverride = localStorage.getItem(UNSAFE_ICON_SIZE_OVERRIDE_LS_KEY);
if (iconSizeOverride != null && (iconSizeOverride === 'v1' || iconSizeOverride === 'v2')) {
  defaultIconSizeVersion = iconSizeOverride;
}

const globalThemeManager = new GlobalThemeManager();

export const plumaContextValueStore = atom<Partial<PlumaContextValue> | null>({
  componentConfig: {
    PlumaIcon: {
      defaultSizeVersion: defaultIconSizeVersion,
    },
    PlumaSnackbar: {
      snackbarApi,
    },
    PlumaCodeBlock: {
      shouldMatchColorScheme: true,
    },
  },
  iconManager,
  globalThemeManager,
});

// TODO: Remove hydra-pluma-state once child apps are updated to use the new stores
setPlumaContextValue(plumaContextValueStore.get() ?? {});
legacyPlumaIconManagerStore.subscribe((value) => {
  plumaContextValueStore.set(merge({}, plumaContextValueStore.get(), { iconManager: value }));
});
legacyPlumaContextValueStore.subscribe((value) => {
  plumaContextValueStore.set(merge({}, plumaContextValueStore.get(), value));
});

export const plumaThemeStore = atom<PlumaTheme | null>(defaultTheme);
const initialTheme = plumaThemeStore.get();
if (initialTheme != null) {
  setPlumaTheme(initialTheme);
}
legacyPlumaThemeStore.subscribe((value) => {
  plumaThemeStore.set(value);
});

/**
 * Mirrors the `colorScheme` and `themes` props passed to the host
 * PlumaProvider. Embedded apps subscribe to these to render their own
 * PlumaProvider with a matching scheme/theme without re-mounting.
 *
 * - `plumaColorSchemePreferenceStore` holds the user's preference
 *   (`'auto' | 'light' | 'dark'`), matching `usePlumaColorSchemePreference()`.
 * - `plumaColorSchemeStore` holds the resolved scheme
 *   (`'light' | 'dark'`), matching `usePlumaColorScheme()`. When the
 *   preference is `'auto'`, this reflects the system-resolved scheme.
 */
type PlumaThemes = { light?: PlumaTheme | PartialPlumaTheme; dark?: PlumaTheme | PartialPlumaTheme };
export const plumaColorSchemePreferenceStore = atom<ColorSchemePreference>('light');
export const plumaColorSchemeStore = atom<ResolvedColorScheme>('light');
export const plumaThemesStore = atom<PlumaThemes>(defaultPlumaThemes);

/** PlumaLinkDefinition connects Pluma links to TanStack Router for internal navigation. */
type LinkComponentProps = React.ComponentPropsWithoutRef<LinkComponent>;

interface PlumaLinkProps extends LinkComponentProps {
  'aria-current'?: React.AriaAttributes['aria-current'];
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLAnchorElement>) => void;
  onMouseEnter?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  onTouchStart?: (event: React.TouchEvent<HTMLAnchorElement>) => void;
}

function PlumaLinkDefinition({ ref, ...props }: PlumaLinkProps & { ref?: Ref<HTMLAnchorElement> }) {
  const { href } = props;

  // Automatically append masq param if masquerading
  const finalHref = !isEmptyString(href) ? appendMasqParam(href) : '';

  if (isEmptyString(finalHref) || isExternalHref(finalHref)) {
    return <RawPlumaLinkDefinition {...props} href={finalHref} ref={ref} />;
  }

  return <InternalPlumaLinkDefinition {...props} href={finalHref} ref={ref} />;
}

function RawPlumaLinkDefinition({ ref, ...props }: PlumaLinkProps & { ref?: Ref<HTMLAnchorElement> }) {
  const { children, href, onClick, replace: _replace, ...restProps } = props;

  const onLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);

    if (event.defaultPrevented) {
      return;
    }

    if (isEmptyString(href)) {
      event.preventDefault();
    }
  };

  return (
    // oxlint-disable-next-line pluma/enforce-pluma-elements -- PlumaProvider linkComponent must render the native anchor adapter.
    <a href={href} {...restProps} onClick={onLinkClick} ref={ref}>
      {children}
    </a>
  );
}

function InternalPlumaLinkDefinition({ ref, ...props }: PlumaLinkProps & { ref?: Ref<HTMLAnchorElement> }) {
  const { children, href, onClick, onFocus, onMouseEnter, onTouchStart, replace, ...restProps } = props;
  const router = useRouter();
  const safeNavigate = useOptionalSafeNavigate();
  const currentPathname = useLocation({ select: (location) => location.pathname });
  const targetHref = href ?? '';
  const targetPathname = targetHref.split(/[#?]/)[0];
  const preloadedHrefRef = useRef<string | null>(null);

  const onLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);

    if (event.defaultPrevented || !shouldProcessLinkClick(event)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (safeNavigate != null) {
      safeNavigate(targetHref, {
        shouldReplaceHistoryEntry: replace,
      });
      return;
    }

    void router.navigate({ to: targetHref, replace });
  };

  const preloadRoute = () => {
    if (preloadedHrefRef.current === targetHref) {
      return;
    }

    preloadedHrefRef.current = targetHref;
    void router.preloadRoute({ to: targetHref }).catch(() => {
      preloadedHrefRef.current = null;
    });
  };

  const onLinkFocus = (event: React.FocusEvent<HTMLAnchorElement>) => {
    onFocus?.(event);

    if (!event.defaultPrevented) {
      preloadRoute();
    }
  };

  const onLinkMouseEnter = (event: React.MouseEvent<HTMLAnchorElement>) => {
    onMouseEnter?.(event);

    if (!event.defaultPrevented) {
      preloadRoute();
    }
  };

  const onLinkTouchStart = (event: React.TouchEvent<HTMLAnchorElement>) => {
    onTouchStart?.(event);

    if (!event.defaultPrevented) {
      preloadRoute();
    }
  };

  return (
    // oxlint-disable-next-line pluma/enforce-pluma-elements -- PlumaProvider linkComponent must render the native anchor adapter.
    <a
      href={targetHref}
      {...restProps}
      aria-current={currentPathname === targetPathname ? 'page' : restProps['aria-current']}
      onClick={onLinkClick}
      onFocus={onLinkFocus}
      onMouseEnter={onLinkMouseEnter}
      onTouchStart={onLinkTouchStart}
      ref={ref}
    >
      {children}
    </a>
  );
}

function isExternalHref(href: string) {
  return /^(mailto:|tel:|https?:\/\/)/.test(href);
}

/**
 * Mirrors the resolved color scheme into `plumaColorSchemeStore` and the
 * legacy hydra-pluma-state theme store so embedded/child apps that
 * subscribe get the matching light/dark theme. Must render inside
 * PlumaProvider so it can read the resolved scheme.
 */
type ResolvedColorSchemeSyncProps = {
  themes: ResolvedPlumaThemes;
};

function ResolvedColorSchemeSync({ themes }: ResolvedColorSchemeSyncProps) {
  const resolvedColorScheme = usePlumaColorScheme();

  useEffect(() => {
    const storedThemes = plumaThemesStore.get();
    if (storedThemes.light !== themes.light || storedThemes.dark !== themes.dark) {
      plumaThemesStore.set(themes);
    }

    if (plumaColorSchemeStore.get() !== resolvedColorScheme) {
      plumaColorSchemeStore.set(resolvedColorScheme);
    }

    const resolvedTheme = themes[resolvedColorScheme];
    plumaThemeStore.set(resolvedTheme);
    setPlumaTheme(resolvedTheme);
  }, [resolvedColorScheme, themes]);

  return null;
}

export function ConnectedPlumaProvider(props: PlumaProviderProps) {
  const plumaContextValue = useStore(plumaContextValueStore);
  const themeContext = useThemeContext();
  const isDarkerDarkThemeEnabled = useFeatureFlag(AccountFeatureFlag.DarkerDarkTheme);
  const themes = useMemo(() => getPlumaThemes(isDarkerDarkThemeEnabled), [isDarkerDarkThemeEnabled]);

  useEffect(() => {
    if (plumaColorSchemePreferenceStore.get() !== themeContext.colorScheme) {
      plumaColorSchemePreferenceStore.set(themeContext.colorScheme);
    }
  }, [themeContext.colorScheme]);

  useEffect(() => {
    let newValue = merge({}, plumaContextValueStore.get());
    let hasUpdated = false;

    if (plumaContextValue?.componentConfig?.PlumaIcon?.iconNameOverrides?.['design-studio'] !== 'design-studio-new') {
      newValue = merge(newValue, {
        componentConfig: {
          PlumaIcon: {
            iconNameOverrides: {
              'design-studio': 'design-studio-new',
            },
          },
        },
      });
      hasUpdated = true;
    }

    if (plumaContextValue?.componentConfig?.PlumaButton?.unsafe_withSoftDeprecatedSecondaryVariant !== true) {
      newValue = merge(newValue, {
        componentConfig: {
          PlumaButton: {
            unsafe_withSoftDeprecatedSecondaryVariant: true,
          },
        },
      });
      hasUpdated = true;
    }

    if (plumaContextValue?.componentConfig?.PlumaCodeBlock?.shouldMatchColorScheme !== true) {
      newValue = merge(newValue, {
        componentConfig: {
          PlumaCodeBlock: {
            shouldMatchColorScheme: true,
          },
        },
      });
      hasUpdated = true;
    }

    if (hasUpdated) {
      legacyPlumaContextValueStore.set(newValue);
      plumaContextValueStore.set(newValue);
    }
  }, [
    plumaContextValue?.componentConfig?.PlumaButton?.unsafe_withSoftDeprecatedSecondaryVariant,
    plumaContextValue?.componentConfig?.PlumaCodeBlock?.shouldMatchColorScheme,
    plumaContextValue?.componentConfig?.PlumaIcon?.iconNameOverrides?.['design-studio'],
  ]);

  const { children, ...restProps } = props;

  return (
    <PlumaProvider
      colorScheme={themeContext.colorScheme}
      themes={themes}
      iconManager={plumaContextValue?.iconManager ?? iconManager}
      componentFlags={plumaContextValue?.componentFlags}
      componentConfig={plumaContextValue?.componentConfig}
      shouldApplyCssIsolation={true}
      linkComponent={PlumaLinkDefinition as LinkComponent}
      withBaselineStyles={true}
      withGlobalVariables={true}
      withGlobalResetStyles={true}
      globalThemeManager={plumaContextValue?.globalThemeManager ?? globalThemeManager}
      {...restProps}
    >
      <ResolvedColorSchemeSync themes={themes} />
      {children}
    </PlumaProvider>
  );
}
